import React, { useState } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
  slug?: string;
}

interface UserProfileProps {
  user: User;
  onUpdate: (user: User) => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ user, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(user.name);
  const [slug, setSlug] = useState(user.slug || '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setError(null);
    setSaving(true);

    try {
      const response = await fetch('/api/v1/users/profile', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user: { name, slug } }),
      });

      if (response.ok) {
        const data = await response.json();
        onUpdate(data.user);
        setIsEditing(false);
      } else {
        const data = await response.json();
        setError(data.errors?.join(', ') || 'Failed to update profile');
      }
    } catch (err) {
      setError('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setName(user.name);
    setSlug(user.slug || '');
    setError(null);
    setIsEditing(false);
  };

  const handleSlugChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9\-_]/g, '');
    setSlug(sanitized);
  };

  if (isEditing) {
    return (
      <div className="p-6 border-b border-gray-700">
        <div className="space-y-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
              placeholder="Your name"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 mb-1">
              Username
              <span className="text-gray-500 ml-1">(for public URLs)</span>
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
              placeholder="username"
            />
            {slug && (
              <div className="mt-1 text-xs text-gray-500">
                crudo.com/{slug}
              </div>
            )}
          </div>

          {error && (
            <div className="text-xs text-red-400">{error}</div>
          )}

          <div className="flex space-x-2">
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm rounded transition-colors"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white text-sm rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 border-b border-gray-700">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-white font-medium truncate">{user.name}</div>
          <div className="text-xs text-gray-400 truncate">{user.email}</div>
          {user.slug && (
            <div className="text-xs text-gray-500 mt-1">@{user.slug}</div>
          )}
        </div>
        <button
          onClick={() => setIsEditing(true)}
          className="ml-2 text-xs text-gray-400 hover:text-white"
        >
          Edit
        </button>
      </div>
    </div>
  );
};
